import React, { useState, useContext, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, query, where, addDoc } from 'firebase/firestore';
import { db } from '../firebase-config';
import { AuthContext } from '../auth-context';
import LOGO from "../assets/MF-CPU-LOGO.png";

import {
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Paper,
  Container,
  Fade,
  alpha,
  useMediaQuery,
  Grid,
  Avatar,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import LoginIcon from '@mui/icons-material/Login';
import LockPersonIcon from '@mui/icons-material/LockPerson';
import PersonIcon from '@mui/icons-material/Person';
import SecurityIcon from '@mui/icons-material/Security';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';

const LoginScreen = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'lg'));
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);

  /* ===========================
     🔐 STATE MANAGEMENT
  ============================ */
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  /* ===========================
     🔐 LOGIN HANDLER
  ============================ */
  const handleLogin = useCallback(async (e) => {
    if (e) {
      e.preventDefault(); // Prevent default form submission
    }
    
    setError('');

    if (!username || !password) {
      setError('Username and password are required');
      return;
    }

    setLoading(true);

    try {
      const q = query(
        collection(db, 'adminUsers'),
        where('userName', '==', username),
        where('password', '==', password)
      );

      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const userData = querySnapshot.docs[0].data();
        const user = {
          username,
          role: userData.role || 'admin',
          id: querySnapshot.docs[0].id
        };

        login(user);
        setTimeout(() => navigate('/dashboard'), 300);
      } else {
        setError('Incorrect username or password');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [username, password, login, navigate]);

  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault(); // Prevent form submission
      handleLogin();
    }
  }, [handleLogin]);

  const handleUsernameChange = useCallback((e) => {
    setUsername(e.target.value);
  }, []);

  const handlePasswordChange = useCallback((e) => {
    setPassword(e.target.value);
  }, []);

  const toggleShowPassword = useCallback(() => {
    setShowPassword(prev => !prev);
  }, []);

  /* ===========================
     🎨 MOBILE LAYOUT
  ============================ */
  if (isMobile) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: `
            linear-gradient(135deg, 
              ${theme.palette.primary.main} 0%, 
              ${theme.palette.secondary.main} 100%
            )
          `,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `radial-gradient(circle at 30% 20%, ${alpha(theme.palette.common.white, 0.15)} 0%, transparent 50%),
                        radial-gradient(circle at 70% 80%, ${alpha(theme.palette.common.white, 0.1)} 0%, transparent 50%)`,
          }}
        />

        <Container maxWidth="xs" sx={{ height: '100vh', display: 'flex', alignItems: 'center' }}>
          <Paper
            sx={{
              width: '100%',
              borderRadius: 4,
              padding: 3,
              background: alpha(theme.palette.background.paper, 0.95),
              backdropFilter: 'blur(20px)',
              border: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
              boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
            }}
          >
            {/* Mobile Header */}
            <Box textAlign="center" mb={3}>
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  margin: '0 auto 16px',
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                }}
              >
                <img
                  src={LOGO}
                  alt="MF"
                  style={{ width: '90%', height: 50, filter: 'brightness(0) invert(1)' }}
                />
              </Avatar>
              
              <Typography variant="h5" fontWeight={700} gutterBottom>
                Welcome Back
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Sign in to continue
              </Typography>
            </Box>

            {/* Mobile Form */}
            <Box>
              <TextField
                fullWidth
                label="Username"
                margin="normal"
                size="small"
                value={username}
                onChange={handleUsernameChange}
                onKeyDown={handleKeyPress}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />

              <TextField
                fullWidth
                type={showPassword ? 'text' : 'password'}
                label="Password"
                margin="normal"
                size="small"
                value={password}
                onChange={handlePasswordChange}
                onKeyDown={handleKeyPress}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockPersonIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={toggleShowPassword}
                        onMouseDown={(e) => e.preventDefault()}
                      >
                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />

              {error && (
                <Fade in={!!error}>
                  <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
                    {error}
                  </Alert>
                </Fade>
              )}

              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={handleLogin}
                disabled={loading}
                sx={{
                  mt: 3,
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: 600,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  boxShadow: '0 8px 20px rgba(0,0,0,0.2)',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 12px 24px rgba(0,0,0,0.3)',
                  },
                  '&:active': {
                    transform: 'translateY(0)',
                  },
                }}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  <>
                    <LoginIcon sx={{ mr: 1 }} />
                    Sign In
                  </>
                )}
              </Button>

              <Box mt={3} textAlign="center">
                <SmartphoneIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  Mobile Version • Madras Flavours
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Container>
      </Box>
    );
  }

  /* ===========================
     🎨 DESKTOP/TABLET LAYOUT
  ============================ */
  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: `
          linear-gradient(135deg, 
            ${theme.palette.background.default} 0%, 
            ${alpha(theme.palette.primary.light, 0.1)} 100%
          )
        `,
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `
            radial-gradient(circle at 15% 50%, ${alpha(theme.palette.primary.main, 0.05)} 0%, transparent 20%),
            radial-gradient(circle at 85% 30%, ${alpha(theme.palette.secondary.main, 0.05)} 0%, transparent 20%),
            radial-gradient(circle at 50% 80%, ${alpha(theme.palette.primary.light, 0.03)} 0%, transparent 25%)
          `,
        }}
      />

      <Container maxWidth="lg">
        <Grid container spacing={4} alignItems="center">
          {/* Left Panel - Brand/Info */}
          <Grid item xs={12} md={6}>
            <Box sx={{ 
              padding: 4,
              animation: 'slideInLeft 0.8s ease-out',
              '@keyframes slideInLeft': {
                from: { opacity: 0, transform: 'translateX(-50px)' },
                to: { opacity: 1, transform: 'translateX(0)' },
              }
            }}>
              <Box sx={{ mb: 4 }}>
                <img
                  src={LOGO}
                  alt="Madras Flavours Central Kitchen"
                  style={{ 
                    height: 60,
                    marginBottom: 24,
                    filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.1))'
                  }}
                />
                <Typography variant="h2" fontWeight={800} gutterBottom sx={{
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontSize: isTablet ? '2rem' : '2.75rem',
                  lineHeight: 1.2,
                }}>
                  Central Kitchen Management System
                </Typography>
                <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
                  Streamline your kitchen operations with our comprehensive management platform
                </Typography>
              </Box>

              <Box sx={{ 
                background: alpha(theme.palette.background.paper, 0.8),
                borderRadius: 3,
                padding: 3,
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SecurityIcon color="primary" />
                  Secure Access Features
                </Typography>
                <Box component="ul" sx={{ pl: 2, mb: 0 }}>
                  <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Role-based access control
                  </Typography>
                  <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Real-time activity monitoring
                  </Typography>
                  <Typography component="li" variant="body2" color="text.secondary">
                    Encrypted data transmission
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ mt: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
                <DesktopWindowsIcon color="primary" />
                <Typography variant="caption" color="text.secondary">
                  Desktop Optimized • Enhanced for large screens
                </Typography>
              </Box>
            </Box>
          </Grid>

          {/* Right Panel - Login Form */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={8}
              sx={{
                maxWidth: 440,
                margin: '0 auto',
                borderRadius: 4,
                padding: 4,
                background: theme.palette.background.paper,
                boxShadow: '0 20px 60px rgba(0,0,0,0.1)',
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 4,
                  background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  borderTopLeftRadius: 4,
                  borderTopRightRadius: 4,
                }
              }}
            >
              <Box textAlign="center" mb={3}>
                <Typography variant="h4" fontWeight={700} gutterBottom>
                  Admin Login
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Enter your credentials to access the dashboard
                </Typography>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Box mb={3}>
                  <TextField
                    fullWidth
                    label="Username"
                    value={username}
                    onChange={handleUsernameChange}
                    onKeyDown={handleKeyPress}
                    disabled={loading}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        transition: 'all 0.3s ease',
                        '&.Mui-focused': {
                          boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
                        },
                      }
                    }}
                  />
                </Box>

                <Box mb={4}>
                  <TextField
                    fullWidth
                    type={showPassword ? 'text' : 'password'}
                    label="Password"
                    value={password}
                    onChange={handlePasswordChange}
                    onKeyDown={handleKeyPress}
                    disabled={loading}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={toggleShowPassword}
                            onMouseDown={(e) => e.preventDefault()}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        transition: 'all 0.3s ease',
                        '&.Mui-focused': {
                          boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
                        },
                      }
                    }}
                  />
                </Box>

                {error && (
                  <Fade in={!!error}>
                    <Alert severity="error" sx={{ borderRadius: 2, mb: 3 }}>
                      {error}
                    </Alert>
                  </Fade>
                )}

                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={handleLogin}
                  disabled={loading}
                  sx={{
                    py: 1.5,
                    borderRadius: 2,
                    fontWeight: 600,
                    fontSize: '1rem',
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.3)}`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 12px 28px ${alpha(theme.palette.primary.main, 0.4)}`,
                    },
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    <>
                      <LoginIcon sx={{ mr: 1.5 }} />
                      Sign In to Dashboard
                    </>
                  )}
                </Button>
              </Box>

              <Box sx={{ 
                borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                pt: 2,
                textAlign: 'center',
              }}>
                <Typography variant="caption" color="text.secondary">
                  <SecurityIcon fontSize="inherit" sx={{ mr: 0.5, verticalAlign: 'text-bottom' }} />
                  © {new Date().getFullYear()} Madras Flavours • Version 3.2
                </Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default LoginScreen;